import React, { useState, useEffect } from 'react';
import { Button, Text, Card, Badge, MessageBar, MessageBarBody } from '@fluentui/react-components';
import {
    VideoClip24Regular,
    Call24Regular,
    Bot24Regular,
    Document24Regular,
    Settings24Regular,
} from '@fluentui/react-icons';
import { teamsService } from '../../../services/teams/teamsService';
import { apiService } from '../../../services/api/apiService';

interface TeamsConsultationProps {
    consultationId: string;
    onConsultationEnd: () => void;
}

export const TeamsConsultationInterface: React.FC<TeamsConsultationProps> = ({
    consultationId,
    onConsultationEnd,
}) => {
    const [consultation, setConsultation] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [botActive, setBotActive] = useState(false);
    const [consultationStarted, setConsultationStarted] = useState(false);

    useEffect(() => {
        loadConsultationData();
    }, [consultationId]);

    const loadConsultationData = async () => {
        try {
            setLoading(true);
            const result = await apiService.getConsultation(consultationId);

            if (result.success) {
                setConsultation(result.consultation);
                setConsultationStarted(result.consultation.status === 'in_progress');
                setBotActive(result.consultation.teamsStatus === 'in_progress');
            } else {
                setError('Failed to load consultation details');
            }
        } catch (error) {
            console.error('Error loading consultation:', error);
            setError('Failed to load consultation');
        } finally {
            setLoading(false);
        }
    };

    const handleStartConsultation = async () => {
        try {
            setLoading(true);
            const result = await teamsService.startCall({
                consultationId,
                patientName: consultation?.patientName,
                interpreterRequired: consultation?.interpreterRequired,
                language: consultation?.language || 'English',
            });

            if (result.success) {
                setConsultationStarted(true);
                setBotActive(true);
                await loadConsultationData(); // Refresh data
            } else {
                setError(result.error || 'Failed to start consultation');
            }
        } catch (error) {
            console.error('Error starting consultation:', error);
            setError('Failed to start consultation');
        } finally {
            setLoading(false);
        }
    };

    const handleEndConsultation = async () => {
        try {
            setLoading(true);
            await teamsService.endConsultation(consultationId, 'Consultation completed');
            onConsultationEnd();
        } catch (error) {
            console.error('Error ending consultation:', error);
            setError('Failed to end consultation');
        } finally {
            setLoading(false);
        }
    };

    const handleJoinTeamsMeeting = () => {
        if (consultation?.teamsJoinUrl) {
            if (teamsService.isInTeamsEnvironment()) {
                teamsService.joinMeeting(consultation.teamsJoinUrl);
            } else {
                window.open(consultation.teamsJoinUrl, '_blank');
            }
        }
    };

    if (loading && !consultation) {
        return (
            <div
                style={{
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f3f2f1',
                }}
            >
                <Text size={500}>Loading consultation...</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    backgroundColor: '#f3f2f1',
                }}
            >
                <Text size={600} style={{ color: '#d13438' }}>
                    ⚠️ Error
                </Text>
                <Text size={400}>{error}</Text>
                <Button appearance="primary" onClick={() => window.history.back()}>
                    Return to Dashboard
                </Button>
            </div>
        );
    }

    return (
        <div
            style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#f3f2f1',
                padding: '24px',
            }}
        >
            {/* Header */}
            <Card style={{ marginBottom: '24px', padding: '16px' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <div>
                        <Text size={600} weight="semibold">
                            Teams Consultation - {consultation?.patientName}
                        </Text>
                        <div
                            style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                marginTop: '8px',
                            }}
                        >
                            <Badge
                                appearance={consultationStarted ? 'filled' : 'outline'}
                                color={consultationStarted ? 'success' : 'important'}
                            >
                                {consultationStarted ? 'Active' : 'Pending'}
                            </Badge>
                            {botActive && (
                                <Badge appearance="filled" color="informative">
                                    AI Assistant Active
                                </Badge>
                            )}
                            {consultation?.interpreterRequired && (
                                <Badge appearance="outline">
                                    {consultation.language} Interpreter
                                </Badge>
                            )}
                        </div>
                    </div>
                    <Button
                        appearance="subtle"
                        icon={<Settings24Regular />}
                        onClick={() => window.history.back()}
                    >
                        Return to Dashboard
                    </Button>
                </div>
            </Card>

            {/* Main Content */}
            <div
                style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 300px', gap: '24px' }}
            >
                {/* Left Panel - Teams Meeting */}
                <Card style={{ padding: '24px', display: 'flex', flexDirection: 'column' }}>
                    <div
                        style={{
                            textAlign: 'center',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: '24px',
                        }}
                    >
                        {consultation?.teamsJoinUrl ? (
                            <>
                                <div>
                                    <VideoClip24Regular
                                        style={{ fontSize: '64px', color: '#0078d4' }}
                                    />
                                    <Text
                                        size={600}
                                        style={{ display: 'block', marginTop: '16px' }}
                                    >
                                        Teams Meeting Ready
                                    </Text>
                                    <Text size={400} style={{ color: '#605e5c', marginTop: '8px' }}>
                                        Join the consultation using Microsoft Teams
                                    </Text>
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '16px',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {!consultationStarted ? (
                                        <Button
                                            appearance="primary"
                                            size="large"
                                            icon={<Call24Regular />}
                                            onClick={handleStartConsultation}
                                            disabled={loading}
                                        >
                                            Start Consultation
                                        </Button>
                                    ) : (
                                        <>
                                            <Button
                                                appearance="primary"
                                                size="large"
                                                icon={<VideoClip24Regular />}
                                                onClick={handleJoinTeamsMeeting}
                                            >
                                                Join Teams Meeting
                                            </Button>
                                            <Button
                                                appearance="secondary"
                                                size="large"
                                                onClick={handleEndConsultation}
                                                disabled={loading}
                                            >
                                                End Consultation
                                            </Button>
                                        </>
                                    )}
                                </div>

                                {teamsService.isInTeamsEnvironment() && (
                                    <MessageBar intent="info">
                                        <MessageBarBody>
                                            You're running in Microsoft Teams. The meeting will open
                                            directly in Teams.
                                        </MessageBarBody>
                                    </MessageBar>
                                )}
                            </>
                        ) : (
                            <>
                                <div>
                                    <Text size={600} style={{ color: '#d13438' }}>
                                        No Teams Meeting Available
                                    </Text>
                                    <Text size={400} style={{ color: '#605e5c', marginTop: '8px' }}>
                                        This consultation was not created with Teams integration
                                    </Text>
                                </div>
                                <Button appearance="primary" onClick={() => window.history.back()}>
                                    Return to Dashboard
                                </Button>
                            </>
                        )}
                    </div>
                </Card>

                {/* Right Panel - Consultation Info */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Patient Info */}
                    <Card style={{ padding: '16px' }}>
                        <Text size={500} weight="semibold" style={{ marginBottom: '12px' }}>
                            Patient Information
                        </Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div>
                                <Text size={300} weight="semibold">
                                    Name:
                                </Text>
                                <Text size={300} style={{ marginLeft: '8px' }}>
                                    {consultation?.patientName || 'N/A'}
                                </Text>
                            </div>
                            <div>
                                <Text size={300} weight="semibold">
                                    Type:
                                </Text>
                                <Text size={300} style={{ marginLeft: '8px' }}>
                                    {consultation?.consultationType || 'Psychiatric'}
                                </Text>
                            </div>
                            <div>
                                <Text size={300} weight="semibold">
                                    Priority:
                                </Text>
                                <Text size={300} style={{ marginLeft: '8px' }}>
                                    {consultation?.priority || 'Routine'}
                                </Text>
                            </div>
                            {consultation?.interpreterRequired && (
                                <div>
                                    <Text size={300} weight="semibold">
                                        Language:
                                    </Text>
                                    <Text size={300} style={{ marginLeft: '8px' }}>
                                        {consultation.language}
                                    </Text>
                                </div>
                            )}
                        </div>
                    </Card>

                    {/* AI Features */}
                    <Card style={{ padding: '16px' }}>
                        <Text size={500} weight="semibold" style={{ marginBottom: '12px' }}>
                            AI Features
                        </Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Bot24Regular
                                    style={{ color: botActive ? '#107c10' : '#605e5c' }}
                                />
                                <Text size={300}>
                                    Meeting Bot {botActive ? 'Active' : 'Inactive'}
                                </Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Document24Regular style={{ color: '#0078d4' }} />
                                <Text size={300}>Real-time Transcription</Text>
                            </div>
                            {consultation?.interpreterRequired && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Text size={300}>🌐 Language Translation</Text>
                                </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Text size={300}>🩺 Clinical Analysis</Text>
                            </div>
                        </div>
                    </Card>

                    {/* Meeting Details */}
                    {consultation?.teamsJoinUrl && (
                        <Card style={{ padding: '16px' }}>
                            <Text size={500} weight="semibold" style={{ marginBottom: '12px' }}>
                                Meeting Details
                            </Text>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <div>
                                    <Text size={300} weight="semibold">
                                        Meeting ID:
                                    </Text>
                                    <Text
                                        size={200}
                                        style={{ fontFamily: 'monospace', marginTop: '4px' }}
                                    >
                                        {consultation.teamsMeetingId}
                                    </Text>
                                </div>
                                <div>
                                    <Text size={300} weight="semibold">
                                        Scheduled:
                                    </Text>
                                    <Text size={300} style={{ marginTop: '4px' }}>
                                        {consultation.scheduledTime
                                            ? new Date(consultation.scheduledTime).toLocaleString()
                                            : 'N/A'}
                                    </Text>
                                </div>
                                <div>
                                    <Text size={300} weight="semibold">
                                        Status:
                                    </Text>
                                    <Text size={300} style={{ marginTop: '4px' }}>
                                        {consultation.status}
                                    </Text>
                                </div>
                            </div>
                        </Card>
                    )}
                </div>
            </div>
        </div>
    );
};
